/**
 * WHOSE TERMS A SIGN-IN AGREES TO, and where each owner's legal pages live (operator, 2026-09-27: "ifederated should
 * have its own and so should every application which uses ifederated as its auth provider").
 *
 * The sign-in box renders at the iFederated hub for every app, and it used to link ONE set of pages -- the brand's, a
 * real-estate agreement that appoints the brand's brokerage -- whichever app the person came from. The account a person
 * signs into is the hub's, so the hub's own Terms and Privacy Policy are what every sign-in agrees to
 * ({@link HUB_LEGAL_OWNER}); each app that sends people to the hub names ITS owner on its row
 * (`const/single-sign-on-apps.ts`, `SsoAppIdentity.legalOwner`), and the box names that owner's Terms beside them.
 *
 * ONE ROW PER OWNER, not per host: the brand's apps (the apex, admin, analytics, explore, market maker, profile) are one
 * owner under one set of terms, served on the brand's apex. Each owner's pages are ITS OWN SITE's `/legal/*`
 * (`LEGAL_PAGE_ROUTES`), so the address is the owner's origin for the environment the page is in. The owner KEYS are
 * this record's (`LegalOwner`, `types/single-sign-on.ts`, reads them off it).
 */

import { ACTIVE_BRAND } from './brand-registry.ts';
import { brandServiceUrls } from './brand-hostnames.ts';
import { wranglerEnvKeyFor } from './config-environments.ts';
import { IFEDERATED_APP_URLS, IFEDERATED_PRODUCT_NAME } from './ifederated-identity.ts';
import { KENECTING_APP_URLS, KENECTING_PRODUCT_NAME } from './kenecting-identity.ts';
import { NOOR_APP_URLS, NOOR_PRODUCT_NAME } from './noor-identity.ts';
import { FEDERATED_APP_URLS, FEDERATED_PRODUCT_NAME } from './federated-identity.ts';
import { LEGAL_PAGE_ROUTES } from './routes-page.ts';

/** Each owner: the name the consent line says, and its site's origin per environment. */
export const LEGAL_OWNERS = {
	ifederated: { name: IFEDERATED_PRODUCT_NAME, origins: IFEDERATED_APP_URLS },
	brand: { name: ACTIVE_BRAND.name, origins: brandServiceUrls(null) },
	kenecting: { name: KENECTING_PRODUCT_NAME, origins: KENECTING_APP_URLS },
	noor: { name: NOOR_PRODUCT_NAME, origins: NOOR_APP_URLS },
	federated: { name: FEDERATED_PRODUCT_NAME, origins: FEDERATED_APP_URLS },
} as const;

/** The owner whose Terms and Privacy Policy every sign-in agrees to: the account is the hub's. */
export const HUB_LEGAL_OWNER = 'ifederated' satisfies keyof typeof LEGAL_OWNERS;

/**
 * The ABSOLUTE address of one owner's legal page in this environment. Absolute because the box that links it renders
 * on the hub, a different origin from every app's (a relative `/legal/terms` there 404'd, operator 2026-09-22).
 * @runtime-derived-ssot-helper composes {@link LEGAL_OWNERS}' origins with `LEGAL_PAGE_ROUTES`; no second spelling.
 */
export function legalPageUrl(owner: keyof typeof LEGAL_OWNERS, page: keyof typeof LEGAL_PAGE_ROUTES, isProduction: boolean): string {
	return `${LEGAL_OWNERS[owner].origins[wranglerEnvKeyFor(isProduction)]}${LEGAL_PAGE_ROUTES[page]}`;
}
